import { useGetAllExamCentersQuery } from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Flex, Form, FormSearchMultipleSelection } from '@shared/components';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { BLOCKED_TYPE_LABEL_MAP, ALL_BLOCKED_TYPES } from '../../constants';
import { useAdminBlockedUsersListSearchParams } from '../../hooks';

const BLOCKED_TYPE_OPTIONS = ALL_BLOCKED_TYPES.map((key) => ({
  label: BLOCKED_TYPE_LABEL_MAP[key],
  value: key,
}));

const BlockedUserListFilterSchema = z.object({
  blockedTypes: z
    .object({
      label: z.string(),
      value: z.enum(ALL_BLOCKED_TYPES),
    })
    .array(),
  examCenters: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .array(),
});

type BlockedUsersListFilterFormInput = z.infer<typeof BlockedUserListFilterSchema>;

export default function BlockedUsersListFilterSection() {
  const [
    { examCenters: defaultExamCenters, blockedTypes: defaultBlockedTypes },
    { changeBlockedTypes, changeExamCenters },
  ] = useAdminBlockedUsersListSearchParams();

  const { data, loading } = useGetAllExamCentersQuery();
  const examCenterOptions = useMemo(() => {
    return [...(data?.ecGetAllExamCenters?.examCenters ?? [])]
      .sort((a, b) => a.examCenterId - b.examCenterId)
      .map((examCenter) => ({
        label: examCenter.code,
        value: examCenter.code,
      }));
  }, [data]);

  const formContext = useForm<BlockedUsersListFilterFormInput>({
    resolver: zodResolver(BlockedUserListFilterSchema),
    defaultValues: {
      blockedTypes: defaultBlockedTypes.map((type) => ({
        label: BLOCKED_TYPE_LABEL_MAP[type],
        value: type,
      })),
      examCenters: [],
    },
  });
  const { reset, watch, handleSubmit } = formContext;

  const handleReset = () => {
    reset({
      blockedTypes: [],
      examCenters: [],
    });
    handleSubmit(onSubmit)();
  };

  const [blockedTypes, examCenters] = watch(['blockedTypes', 'examCenters']);

  const onSubmit = (data: BlockedUsersListFilterFormInput) => {
    const { blockedTypes, examCenters } = data;
    const blockedTypeValues = blockedTypes.map((type) => type.value);
    changeBlockedTypes(blockedTypeValues);
    const examCenterValues = examCenters.map((center) => center.value);
    changeExamCenters(examCenterValues);
  };

  useEffect(() => {
    // Initially set form values from search params on loaded GetExamCenters data to show correct labels in filter selections
    if (!loading) {
      const defaultFormExamCenters = examCenterOptions.filter((option) =>
        defaultExamCenters.includes(option.value.toString()),
      );
      reset({
        blockedTypes: defaultBlockedTypes.map((type) => ({
          label: BLOCKED_TYPE_LABEL_MAP[type],
          value: type,
        })),
        examCenters: defaultFormExamCenters,
      });
    }
  }, [loading, defaultBlockedTypes, defaultExamCenters, examCenterOptions, reset]);

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          sx={(theme) => ({
            p: theme.spacing(4),
            mt: theme.spacing(4),
            borderRadius: theme.shape.rounded(2),
            border: '1px solid',
            borderColor: theme.palette.grey[300],
            flexDirection: 'column',
            gap: theme.spacing(4),
          })}
        >
          <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
            <FormSearchMultipleSelection
              name='blockedTypes'
              label='ประเภท'
              options={BLOCKED_TYPE_OPTIONS}
              placeholder={blockedTypes.length ? '' : 'แสดงทั้งหมด'}
              sx={{ width: '259px' }}
              limitTags={-1}
            />
            <FormSearchMultipleSelection
              name='examCenters'
              label='ศูนย์ทดสอบ'
              options={examCenterOptions}
              loading={loading}
              placeholder={examCenters.length ? '' : 'แสดงทั้งหมด'}
              sx={{ width: '259px' }}
              limitTags={-1}
            />
          </Flex>
          <Divider />
          <Flex sx={{ gap: (theme) => theme.spacing(2), justifyContent: 'end' }}>
            <Button color='secondary' variant='outlined' onClick={handleReset}>
              Reset
            </Button>
            <Button type='submit'>Apply</Button>
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
